import React, { useId, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, HelpCircle, Loader2, PlusCircle, RefreshCw } from "lucide-react";

import "../styles/learning-match.css";
import CandidateList, { kindTag, sourceTag } from "./CandidateList";
import { TEXT } from "./editorText";
import { MATCH_STATUS, ROW_STATE, choiceAt, chosenAt, issuesAt, rowAt, rowStateAt } from "./matchModel";

const M = TEXT.match;
const FACT_KIND = "fact";
const TONE = Object.freeze({ COMPUTING: "computing", ERROR: "error" });
const ICON = Object.freeze({
  [ROW_STATE.REUSED]: CheckCircle2, [ROW_STATE.CHECK]: HelpCircle, [ROW_STATE.NEW]: PlusCircle, [TONE.COMPUTING]: Loader2, [TONE.ERROR]: AlertCircle,
});
const ICON_SIZE = 16;
const CARET_SIZE = 14;

/** The line under the text: what the row maps to (full catalog text, its source) or why it is new. */
function SubLine({ tone, row, chosen, candidates }) {
  if (tone === TONE.COMPUTING) return <span className="le-mrow-sub-skel" aria-hidden="true" />;
  if (tone === TONE.ERROR) return <span>{M.failedRow}</span>;
  if (!chosen) {
    if (!row) return <span>{M.noMatchData}</span>;
    return <span>{candidates.length ? M.willBeNew : M.noCandidates}</span>;
  }
  const tag = sourceTag(chosen);
  const kind = kindTag({ kind: chosen.kind || (row && row.matched_kind) });
  const reuses = chosen.kind === FACT_KIND || (row && row.matched_kind === FACT_KIND) ? M.reusesFact : M.reuses;
  return (
    <>
      <span className="le-mrow-sub-key">{tone === ROW_STATE.CHECK ? M.possible(chosen.label) : reuses(chosen.label)}</span>
      {kind && <span className="le-tag">{kind}</span>}
      {tag && <span className="le-tag le-tag--muted">{tag}</span>}
      {tone === ROW_STATE.CHECK && <span>{M.confirmHint}</span>}
    </>
  );
}

/** The match control: a skeleton while matching, Retry on failure, else a status pill and, beside it but separate, a "Change" button that opens the candidate list. */
function Control({ tone, text, chosen, candidates, onRetry, open, toggle, listId, chipRef }) {
  // While the list is open its blur closes it before the click lands, so the click would reopen it: keep focus on the list instead.
  const keepFocus = (event) => { if (open) event.preventDefault(); };
  if (tone === TONE.COMPUTING) return <span className="le-mbtn le-mbtn--skel" role="presentation" />;
  if (tone === TONE.ERROR) {
    return (
      <button type="button" className="le-btn le-btn--sm le-mbtn--error" onClick={onRetry}>
        <RefreshCw size={CARET_SIZE} aria-hidden="true" /><span className="le-mbtn-state">{M.retry}</span>
      </button>
    );
  }
  const state = M.state[tone];
  const disabled = !candidates.length;
  const current = chosen ? chosen.label : M.willBeNew;
  return (
    <>
      <span className={`le-pill le-pill--${tone}`} role="status">{state}</span>
      {!disabled && (
        <button type="button" ref={chipRef} className="le-btn le-btn--sm le-mbtn--change"
          title={M.changeFor(text)} aria-label={`${M.changeFor(text)}. ${state}: ${current}`}
          aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined} onMouseDown={keepFocus} onClick={toggle}>
          {M.change}<ChevronDown size={CARET_SIZE} aria-hidden="true" />
        </button>
      )}
    </>
  );
}

/**
 * The row anatomy every matched line shares, on the Review screen and in the Learning Catalog's stored snapshot:
 * [state glyph] [the line, with what it maps to beneath] [control]; `children` (a candidate list, issues) span the row below.
 * `tone` is a ROW_STATE or TONE value and picks the glyph (`icon` overrides it) and the colouring.
 */
export function RowShell({ tone, icon, text, sub, control, children }) {
  const Icon = icon || ICON[tone];
  return (
    <li className={`le-mrow le-mrow--${tone}`}>
      <span className="le-mrow-glyph" aria-hidden="true">{Icon && <Icon size={ICON_SIZE} />}</span>
      <div className="le-mrow-body">
        <p className="le-mrow-text">{text}</p>
        <p className="le-mrow-sub">{sub}</p>
      </div>
      <div className="le-mrow-ctl">{control}</div>
      {children}
    </li>
  );
}

/**
 * One row of the Review screen, the same for a symptom, a step, the root cause and the fix: [state glyph] [the line, with what
 * it maps to beneath] [match control]; the candidate list opens under the row. Props: section, index, text, match
 * (preview.match), onPick(section, index, decision, id), onRetry.
 */
export default function MatchRow({ section, index, text, match, onPick, onRetry }) {
  const listId = useId();
  const chipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const row = rowAt(match, section, index);
  const candidates = (row && row.candidates) || [];
  const chosen = chosenAt(match, section, index);
  let tone = rowStateAt(match, section, index);
  if (match.status === MATCH_STATUS.LOADING) tone = TONE.COMPUTING;
  else if (match.status === MATCH_STATUS.ERROR) tone = TONE.ERROR;
  const issues = issuesAt(match, section, index);

  const close = (refocus) => {
    setOpen(false);
    if (refocus && chipRef.current) chipRef.current.focus();
  };
  return (
    <RowShell tone={tone} text={text} sub={<SubLine tone={tone} row={row} chosen={chosen} candidates={candidates} />}
      control={<Control tone={tone} text={text} chosen={chosen} candidates={candidates} onRetry={onRetry} open={open}
        toggle={() => (open ? close(true) : setOpen(true))} listId={listId} chipRef={chipRef} />}>
      {open && (
        <CandidateList id={listId} line={text} candidates={candidates} value={choiceAt(match, section, index)}
          onPick={(decision, id) => onPick(section, index, decision, id)} onClose={close} />
      )}
      {issues.map((issue, i) => (
        <p key={`${issue.code}-${i}`} className={issue.blocking ? "le-error" : "le-note le-note--blocked"} role={issue.blocking ? "alert" : undefined}>
          {issue.message}
        </p>
      ))}
    </RowShell>
  );
}
